import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { userStoreSaga } from './saga';
import { UserStoreState } from './types';

export const initialState: UserStoreState = {
  uid: undefined,
  firstName: undefined,
  lastName: undefined,
  email: undefined,
  profilePic: undefined,
  isEduDomain: undefined,
  subscriptionActive: undefined,
  credits: 0,
  oneOffCredits: 0,
  recurringCredits: 0,
  referralCode: undefined,
  referralQuota: undefined,
  referredBy: undefined,
  isLoadingProfile: false,
  isLoadingCredits: false,
  isLoadingSubscription: false,
  isLoadingReferral: false,
};

const slice = createSlice({
  name: 'userStore',
  initialState,
  reducers: {
    updateName(state, action: PayloadAction<any>) {
      if (action.payload && action.payload.firstName) {
        state.firstName = action.payload.firstName;
      }

      if (action.payload && action.payload.lastName) {
        state.lastName = action.payload.lastName;
      }
    },

    updateProfilePic(state, action: PayloadAction<any>) {
      if (action.payload && action.payload.profilePic) {
        state.profilePic = action.payload.profilePic;
      }
    },

    updateEmail(state, action: PayloadAction<any>) {
      const value = action.payload;
      if (value === undefined || value === '') {
        // Throw warning
        console.warn('Empty Email');
        return;
      }

      console.log('Email: ', action.payload);
      state.email = value;
    },

    updateCredits(state, action: PayloadAction<any>) {
      const value = action.payload;

      if (value === undefined || value === '') {
        // Throw warning
        console.warn('Empty Credits');
        return;
      }

      state.oneOffCredits = action.payload.oneOffCredits;
      state.recurringCredits = action.payload.recurringCredits;
      state.credits =
        (action.payload.oneOffCredits ?? 0) +
        (action.payload.recurringCredits ?? 0);
    },

    updateUserInternal(state, action: PayloadAction<any>) {
      if (!action.payload) {
        console.warn('Empty User Internal Payload');
        return;
      }

      state.isEduDomain = action.payload.isEduDomain;
      state.subscriptionActive = action.payload.subscriptionActive;
    },

    updateReferralState(state, action: PayloadAction<any>) {
      if (!action.payload) {
        console.warn('Empty Referral Payload');
        return;
      }

      state.referralCode = action.payload.referralCode;
      state.referredBy = action.payload.referredBy;

      const quotaLimit = action.payload.quotaLimit;
      const refereeList = action.payload.refereeList;
      if (Array.isArray(refereeList)) {
        const referralQuota = quotaLimit - refereeList.length;
        if (!Number.isNaN(referralQuota)) state.referralQuota = referralQuota;
      } else {
        state.referralQuota = quotaLimit;
      }
    },

    setIsLoadingProfile(state, action: PayloadAction<boolean>) {
      state.isLoadingProfile = action.payload;
    },

    setIsLoadingCredits(state, action: PayloadAction<boolean>) {
      state.isLoadingCredits = action.payload;
    },

    setIsLoadingSubscription(state, action: PayloadAction<boolean>) {
      state.isLoadingSubscription = action.payload;
    },

    setIsLoadingReferral(state, action: PayloadAction<boolean>) {
      state.isLoadingReferral = action.payload;
    },

    resetState(state) {
      state.uid = initialState.uid;
      state.firstName = initialState.firstName;
      state.lastName = initialState.lastName;
      state.email = initialState.email;
      state.profilePic = initialState.profilePic;
      state.credits = initialState.credits;
      state.isEduDomain = initialState.isEduDomain;
      state.subscriptionActive = initialState.subscriptionActive;
      state.referralCode = initialState.referralCode;
      state.referralQuota = initialState.referralQuota;
      state.referredBy = initialState.referredBy;
      state.isLoadingProfile = initialState.isLoadingProfile;
      state.isLoadingCredits = initialState.isLoadingCredits;
      state.isLoadingSubscription = initialState.isLoadingSubscription;
      state.isLoadingReferral = initialState.isLoadingReferral;
    },
  },
});

export const { actions: userStoreActions } = slice;

export const useUserStoreSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: userStoreSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useUserStoreSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
